import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_LOCAL_API_URL;
const STATUSES = ["pending", "processing", "retry", "sent", "failed"];
const DELIVERY_LABELS = {
  unknown: "Ingen händelse ännu",
  accepted: "Accepterat av SES",
  delivered: "Levererat",
  bounced: "Studsat",
  complained: "Spam-anmält",
  delayed: "Fördröjt",
  rejected: "Avvisat",
  opened: "Öppnat",
  clicked: "Klickat"
};
const STATUS_LABELS = {
  pending: "Väntar",
  processing: "Behandlas",
  retry: "Nytt försök väntar",
  sent: "Accepterat av SES",
  failed: "Misslyckat"
};

function authHeaders() {
  return {
    "Content-Type": "application/json",
    token: localStorage.getItem("token")
  };
}

function formatDate(value) {
  if (!value) {
    return "–";
  }
  return new Date(value).toLocaleString("sv-SE");
}

function EmailStatus() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [counts, setCounts] = useState({});
  const [statusFilter, setStatusFilter] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const fetchJobs = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/email-jobs`, {
        headers: authHeaders(),
        params: statusFilter ? { status: statusFilter } : {}
      });
      setJobs(response.data.jobs || []);
      setCounts(response.data.counts || {});
      setError("");
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Kunde inte hämta e-poststatus");
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  async function retryJob(jobId) {
    try {
      await axios.post(`${API_URL}/email-jobs/${jobId}/retry`, {}, { headers: authHeaders() });
      setMessage("Jobbet köades om.");
      setError("");
      await fetchJobs();
    } catch (requestError) {
      setMessage("");
      setError(requestError.response?.data?.message || "Jobbet kunde inte köas om");
    }
  }

  return (
    <div className="flex flex-col items-center px-3">
      <div className="w-full max-w-5xl py-4">
        <div className="theme-card">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-700">Administration</p>
              <h1 className="mt-2 text-lg font-bold text-slate-800">E-poststatus</h1>
              <p className="mt-2 text-sm text-slate-600">
                Statusen ”Accepterat av SES” betyder att Amazon SES har tagit emot meddelandet, inte att mottagaren har fått eller läst det.
              </p>
            </div>
            <button className="theme-button-secondary px-5 py-3 text-sm" onClick={() => navigate("/admin-dashboard")}>
              Till administration
            </button>
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {STATUSES.map((status) => (
              <button
                key={status}
                className={`theme-panel text-left ${statusFilter === status ? "border-sky-500" : ""}`}
                onClick={() => setStatusFilter(statusFilter === status ? "" : status)}
              >
                <div className="text-2xl font-bold text-slate-800">{counts[status] || 0}</div>
                <div className="mt-1 text-xs text-slate-600">{STATUS_LABELS[status]}</div>
              </button>
            ))}
          </div>

          {message && <div className="theme-alert-success mt-4">{message}</div>}
          {error && <div className="theme-alert-error mt-4">{error}</div>}

          <div className="mt-5 space-y-3">
            {jobs.length === 0 && !error && <div className="theme-empty-state">Inga e-postjobb att visa.</div>}
            {jobs.map((job) => (
              <article key={job._id} className="theme-panel">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0">
                    <h2 className="break-words font-semibold text-slate-800">{job.subject}</h2>
                    <p className="mt-1 break-all text-sm text-slate-600">{(job.recipients || []).join(", ")}</p>
                    <dl className="mt-3 grid gap-x-5 gap-y-1 text-xs text-slate-600 sm:grid-cols-2">
                      <div>Status: <strong>{STATUS_LABELS[job.status] || job.status}</strong></div>
                      <div>Försök: <strong>{job.attempts}</strong></div>
                      <div>Skapat: {formatDate(job.createdAt)}</div>
                      <div>Nästa försök: {formatDate(job.nextAttemptAt)}</div>
                      <div>SES MessageId: {job.sesMessageId || "–"}</div>
                      <div>Accepterat: {formatDate(job.sentAt)}</div>
                      <div>Leveransstatus: {DELIVERY_LABELS[job.deliveryStatus || "unknown"]}</div>
                      <div>Leveranshändelse: {formatDate(job.deliveryUpdatedAt)}</div>
                    </dl>
                    {job.lastError && (
                      <p className="mt-3 break-words rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
                        {job.lastError}
                      </p>
                    )}
                    {job.deliverySummary && <p className="mt-2 text-xs text-amber-700">{job.deliverySummary}</p>}
                  </div>
                  {(job.status === "failed" || job.status === "retry") && (
                    <button
                      className="theme-badge-button border-sky-200 text-sky-700"
                      onClick={() => retryJob(job._id)}
                    >
                      Köa om
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmailStatus;
